import {AsyncPipe, NgClass} from '@angular/common';
import {Component, Inject, OnDestroy, OnInit, Renderer2, DOCUMENT} from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';

import {asyncScheduler, combineLatest, from, Observable, observeOn, of, Subject} from 'rxjs';
import {catchError, filter, map, take, takeUntil, tap, finalize, switchMap} from 'rxjs/operators';

import {getDecibelInsightsScript} from 'src/app/helpers/utils/decibel-insights.utils';
import {getLangFromLocale} from './helpers/utils/url.utils';

import {AnalyticsService} from './services/analytics/analytics.service';
import {CountryService} from './services/country/country.service';
import {InternalService} from './services/internal/internal.service';
import {LocalizationService} from './services/localization/localization.service';
import {LoaderService} from 'src/app/services/loader/loader.service';
import {PhoneService} from './services/phone/phone.service';
import {ReservationService} from './services/reservation/reservation.service';
import {SettingsService} from './services/settings/settings.service';
import {UrlParametersService} from './services/urlParameters/url-parameters.service';
import {UserService} from './services/user/user.service';
import {BrandService} from 'src/app/shared/services/brand/brand.service';
import {MapLoaderService} from 'src/app/services/map-loader/map-loader.service';

import {FooterComponent} from './shared/components/footer/footer.component';
import {HeaderComponent} from './shared/components/header/header.component';
import {NotificationsComponent} from './shared/components/notifications/notifications.component';
import {RgLoaderComponent} from 'src/app/shared/components/loader/loader.component';

import {ReservationData} from './shared/models/reservation.model';
import {Room} from './shared/models/room.model';
import RoomSearchParameters from './shared/models/roomSearchParams.model';
import {SettingsResponse} from './shared/models/settingsResponse.model';

import {Brand, REGUS, BRAND_GTM_BASE_URLS, BRAND_ADDITIONAL_GTM_CONTAINERS} from 'src/app/shared/constants/brands.constants';

import {environment} from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    NgClass,
    AsyncPipe,
    RouterOutlet,
    RgLoaderComponent,
    HeaderComponent,
    FooterComponent,
    NotificationsComponent
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  public appInitialized: boolean;
  public showHeader: Observable<boolean>;
  public showFooter: Observable<boolean>;
  private readonly destroyed$: Subject<void> = new Subject<void>();
  private readonly favIcon: HTMLLinkElement = document.querySelector('#favIcon');
  private readonly shortcutIcon: HTMLLinkElement = document.querySelector('#shortcutIcon');

  constructor(
    @Inject(DOCUMENT) private readonly _document,
    private readonly analyticsService: AnalyticsService,
    private readonly countryService: CountryService,
    private readonly internalService: InternalService,
    private readonly localizationService: LocalizationService,
    private readonly phoneService: PhoneService,
    private readonly renderer2: Renderer2,
    private readonly reservationService: ReservationService,
    private readonly router: Router,
    private readonly settingsService: SettingsService,
    private readonly urlParametersService: UrlParametersService,
    private readonly userService: UserService,
    private readonly loader: LoaderService,
    private readonly brandService: BrandService,
    private readonly mapLoaderService: MapLoaderService
  ) {
    // FOCUS on main container of the application every time app route changes - the tab focus will start from the very top
    // replace takeUntil(this.destroyed$) with takeUntilDestroyed in Angular v16
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        const appContent = document.getElementById('app-content');
        if (appContent) {
          appContent.focus();
        }
      });
    this.showHeader = this.internalService.hideHeader$.pipe(
      observeOn(asyncScheduler),
      map((value) => !value)
    );
    this.showFooter = this.internalService.hideFooter$.pipe(
      observeOn(asyncScheduler),
      map((value) => !value)
    );
  }

  ngOnInit(): void {
    this.initGoogleAnalytics();
    this.urlParametersService.initParams();
    this.initializeApp().subscribe();

    window.addEventListener('pagehide', (event) => {
      if (!event.persisted) {
        // Drop the inventory reservation whenever the user leaves the ecom app — covers
        // exits that bypass Angular routing (browser back button, tab close, external
        // redirect). See WEB-39753.
        this.reservationService.releaseActiveReservationBeacon();
        sessionStorage.clear();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initGTM(brand: Brand): void {
    const configs = [
      {
        layerName: 'dataLayer',
        src: this.buildBrandGtmSrc(brand)
      },
      ...(BRAND_ADDITIONAL_GTM_CONTAINERS[brand] ?? []).map(({baseUrl, layerName, gtmIdKey}) => ({
        layerName,
        src: this.buildGtmSrc(baseUrl, environment[gtmIdKey], layerName)
      }))
    ];

    configs.forEach(({layerName, src}) => this.initGTMDataLayer(layerName, src));
  }

  private initGTMDataLayer(layerName: string, src: string): void {
    const globalWindow = window as any;
    const dataLayer = globalWindow[layerName] || [];

    if (!dataLayer.some((event) => event.event === 'gtm.js')) {
      dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
      globalWindow[layerName] = dataLayer;
    }

    this.appendScriptToHead(src);
  }

  private buildBrandGtmSrc(brand: Brand): string {
    const baseUrl = BRAND_GTM_BASE_URLS[brand];

    return this.buildGtmSrc(baseUrl, environment.googleAnalyticsKeys[brand]);
  }

  private buildGtmSrc(baseUrl: string, gtmId: string, layerName = 'dataLayer'): string {
    const dataLayerQuery = layerName !== 'dataLayer' ? `&l=${layerName}` : '';

    return `${baseUrl}?id=${gtmId}${dataLayerQuery}`;
  }

  private appendScriptToHead(src: string): void {
    if (this._document.querySelector(`script[src="${src}"]`)) {
      return;
    }

    const script = this.renderer2.createElement('script');
    this.renderer2.setAttribute(script, 'async', 'true');
    this.renderer2.setAttribute(script, 'src', src);

    const head = this._document.head;
    this.renderer2.insertBefore(head, script, head.firstChild);
  }

  private initGoogleAnalytics(): void {
    const globalWindow = window as any;

    if (globalWindow.ga) {
      return;
    }

    globalWindow['GoogleAnalyticsObject'] = 'ga';
    globalWindow.ga =
      globalWindow.ga ||
      function (...args: unknown[]): void {
        (globalWindow.ga.q = globalWindow.ga.q || []).push(args);
      };
    globalWindow.ga.l = new Date().getTime();

    this.appendScriptToHead('https://www.google-analytics.com/analytics.js');
  }

  private configureTemplateSettings(queryParams): void {
    const lang = getLangFromLocale(queryParams);
    this.localizationService.lang.next(lang);
    document.querySelector('html').setAttribute('lang', lang);
  }

  private trackAnalytics(params): void {
    this.analyticsService.trackUser(
      undefined,
      params.countryName,
      'E_commerce',
      params.locale,
      params['ga-tag-website']
    );
    this.analyticsService.initGTMTracking();
  }

  private initializeApp(): Observable<void> {
    this.loader.start();
    const params = this.urlParametersService.currentParams;
    this.processSessionStorageData();

    this.configureTemplateSettings(params);
    this.trackAnalytics(params);

    return combineLatest([
      this.localizationService.getTranslations(params),
      this.settingsService.getSettings(params),
      this.userService.updateLocation().pipe(catchError(() => of(null))),
      this.phoneService.updatePhoneNumbers().pipe(catchError(() => of(null))),
      this.countryService.loadCountryOptions().pipe(catchError(() => of(null)))
    ]).pipe(
      take(1),
      tap(([, settings, , ,]) => {
        this.initGTM(settings.brand);
        this.brandService.applyBrand(settings.brand, this.renderer2);
        this.applySettings(settings);
      }),
      switchMap(() => from(this.mapLoaderService.load(this.renderer2))),
      map(() => undefined),
      finalize(() => {
        this.loader.stop();
        this.appInitialized = true;
      })
    );
  }

  private processSessionStorageData(): void {
    const reservationData: ReservationData | undefined = JSON.parse(sessionStorage.getItem('reservationData'));
    const selectedRoom: Room | undefined = JSON.parse(sessionStorage.getItem('selectedRoom'));
    const searchParameters: RoomSearchParameters | undefined = JSON.parse(sessionStorage.getItem('searchParameters'));

    if (reservationData && selectedRoom && searchParameters) {
      this.reservationService.processReservationData(selectedRoom, reservationData);
    }
  }

  private applySettings(settings: SettingsResponse): void {
    this.setFavIcon(settings.brand ?? REGUS);
    if (settings.languageSpecifics.isRTL) {
      document.querySelector('html')?.setAttribute('dir', 'rtl');
    }
    if (settings.countrySettings.optimizelyUrl) {
      this.injectScript(settings.countrySettings.optimizelyUrl);
    }
    if (settings.preferences.isDecibelInsights) {
      const countryCode = this.urlParametersService.currentParams.countryCode || 'GB';
      const insightsScript = getDecibelInsightsScript(countryCode);
      if (insightsScript) {
        this.injectScript(false, insightsScript);
      }
    }
  }

  private injectScript(src: boolean | string = false, text = ''): void {
    const script = this.renderer2.createElement('script');
    script.type = 'text/javascript';
    if (src) {
      script.src = src;
    } else {
      script.text = text;
    }
    this.renderer2.appendChild(this._document.body, script);
  }

  private setFavIcon(brand: Brand): void {
    this.favIcon.href = `assets/images/${brand}.ico`;
    this.shortcutIcon.href = `assets/images/${brand}.ico`;
  }
}
